/**
 * Pending checkpoint approval for one job.
 *
 * The runner asks once per `write` or `irreversible` phase, before the
 * phase's first action, and one approval covers the whole phase
 * (apps/agent-runtime/flow_agent_runtime/loop.py). The copy says so:
 * approving is not approving a single click.
 *
 * No note is sent with the decision. The API stores a note in the same
 * column as the runner's proposed action and would overwrite it.
 */
import { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { Check, X } from "lucide-react";

import { useResolveApproval } from "@/lib/hooks/useApprovalsApi";
import { fmtDateTime } from "@/lib/format";

import type { PendingApproval } from "./jobApi";
import { DeniedNotice, ErrorNotice, Panel } from "./parts";
import { BTN, BTN_DANGER, BTN_PRIMARY, isStatus } from "./ui";

type Decision = "approved" | "denied";

interface Props {
  query: UseQueryResult<{ items: PendingApproval[]; total: number }>;
  /** False once the job has finished: nothing can be waiting. */
  live: boolean;
}

function decisionError(error: unknown): string {
  if (isStatus(error, 409)) return "This approval was already decided or has expired.";
  if (isStatus(error, 403)) {
    return "You don't have access to decide approvals. This needs the operator, supervisor or entity admin role.";
  }
  if (isStatus(error, 404)) return "Approval not found. It may have been removed.";
  if (isStatus(error, 429)) return "Too many requests. Wait a minute, then try again.";
  return "The decision was not recorded. Try again.";
}

export function ApprovalPanel({ query, live }: Props) {
  const [lastDecision, setLastDecision] = useState<Decision | null>(null);

  let body: React.ReactNode;
  if (!live) {
    body = <p className="text-sm text-ink-3">The job has finished. Nothing is waiting.</p>;
  } else if (query.isPending) {
    body = <p className="text-sm text-ink-3">Checking for a pending approval…</p>;
  } else if (isStatus(query.error, 403)) {
    body = <DeniedNotice what="approvals" roles="operator, supervisor or entity admin" />;
  } else if (query.isError) {
    body = (
      <ErrorNotice
        message="Approvals could not be loaded."
        error={query.error}
        onRetry={() => query.refetch()}
      />
    );
  } else if (query.data.items.length === 0) {
    body = <p className="text-sm text-ink-3">No approval is waiting on this job.</p>;
  } else {
    body = (
      <ul className="space-y-4">
        {query.data.items.map((a) => (
          <li key={a.id}>
            <ApprovalCard approval={a} onDecided={setLastDecision} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Panel title="Approval">
      {lastDecision && (
        <p role="status" className="mb-3 text-sm text-ink-1">
          {lastDecision === "approved"
            ? "Approved. The runner continues the phase on its next check."
            : "Denied. The runner will not act, and the job ends as failed."}
        </p>
      )}
      {body}
    </Panel>
  );
}

export function ApprovalCard({
  approval,
  onDecided,
}: {
  approval: PendingApproval;
  onDecided?: (d: Decision) => void;
}) {
  const resolve = useResolveApproval();
  const [confirming, setConfirming] = useState(false);

  const expired =
    approval.state !== "pending" ||
    (approval.expiresAt !== null && Date.parse(approval.expiresAt) <= Date.now());
  // An unknown risk level is treated as irreversible.
  const risk = approval.phase?.riskLevel ?? null;
  const needsConfirm = risk === null || risk === "irreversible";

  const decide = (decision: Decision) =>
    resolve.mutate({ id: approval.id, decision }, { onSuccess: () => onDecided?.(decision) });

  return (
    <div className="rounded-md border border-status-approval/40 bg-status-approval-bg p-3">
      <div className="text-sm font-medium text-ink-1">
        {approval.phase
          ? `Phase ${approval.phase.order}: ${approval.phase.name}`
          : "Phase not identified"}
      </div>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
        <dt className="text-ink-3">Risk level</dt>
        <dd className="text-ink-1">{risk ?? "Unknown"}</dd>
        <dt className="text-ink-3">Requested</dt>
        <dd className="text-ink-1">{fmtDateTime(approval.requestedAt)}</dd>
        <dt className="text-ink-3">Expires</dt>
        <dd className="text-ink-1">{fmtDateTime(approval.expiresAt)}</dd>
        <dt className="text-ink-3">Proposed action</dt>
        <dd className="break-all font-mono text-[11px] text-ink-1">
          {approval.notes || "The runner sent no description."}
        </dd>
      </dl>

      {expired ? (
        <p className="mt-3 text-xs text-ink-2">
          This request has expired or was already decided. It can no longer be approved or denied.
        </p>
      ) : (
        <>
          <ul className="mt-3 space-y-1 text-xs text-ink-2">
            <li>
              <strong className="text-ink-1">Approve:</strong> the runner carries out this action
              and the rest of this phase without asking again.
            </li>
            <li>
              <strong className="text-ink-1">Deny:</strong> the runner does not act. The job ends
              as failed.
            </li>
          </ul>

          {resolve.isError && (
            <div className="mt-3">
              <ErrorNotice message={decisionError(resolve.error)} error={resolve.error} />
            </div>
          )}

          {confirming ? (
            <div
              role="alertdialog"
              aria-label="Confirm approval"
              className="mt-3 rounded-sm border border-status-failed/40 bg-paper-3 p-3"
            >
              <p className="text-xs text-ink-1">
                {risk === "irreversible"
                  ? "This phase is irreversible. What the runner does on the portal after you approve cannot be undone from Flow."
                  : "The risk level of this phase is unknown, so treat it as irreversible."}
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  disabled={resolve.isPending}
                  onClick={() => decide("approved")}
                >
                  <Check size={12} aria-hidden /> Confirm approval
                </button>
                <button
                  type="button"
                  className={BTN}
                  disabled={resolve.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Go back
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className={BTN_DANGER}
                disabled={resolve.isPending}
                onClick={() => decide("denied")}
              >
                <X size={12} aria-hidden /> Deny
              </button>
              <button
                type="button"
                className={BTN_PRIMARY}
                disabled={resolve.isPending}
                onClick={() => (needsConfirm ? setConfirming(true) : decide("approved"))}
              >
                <Check size={12} aria-hidden /> Approve
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
